import { useState } from "react";
import { Link, Outlet, useLocation, Navigate } from "react-router-dom";
import { Users, Building2, Inbox, LayoutDashboard, LogOut, Loader2, FileText, UserCog, LucideIcon, User, History, Tag, ChevronDown } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ThemedLogo from "@/components/ThemedLogo";
import { ThemeCustomizer } from "@/components/admin/ThemeCustomizer";
import { ProfileEditDialog } from "@/components/admin/ProfileEditDialog";

interface SubNavItem {
  path: string;
  icon: LucideIcon;
  label: string;
}

interface NavItem {
  path: string;
  icon: LucideIcon;
  label: string;
  exact?: boolean;
  adminOnly?: boolean;
  children?: SubNavItem[];
}

const allNavItems: NavItem[] = [
  { path: "/admin", icon: LayoutDashboard, label: "Dashboard", exact: true },
  {
    path: "/admin/talent",
    icon: Users,
    label: "Talent",
    children: [
      { path: "/admin/talent/specialties", icon: Tag, label: "Specialties" },
    ],
  },
  { path: "/admin/clients", icon: Building2, label: "Clients" },
  { path: "/admin/pages", icon: FileText, label: "Pages" },
  { path: "/admin/inbox", icon: Inbox, label: "Inbox" },
  { path: "/admin/users", icon: UserCog, label: "Users", adminOnly: true },
  { path: "/admin/logs", icon: History, label: "Sign-In Logs", adminOnly: true },
];

const AdminLayout = () => {
  const location = useLocation();
  const { user, isAdmin, hasAdminAccess, isLoading, signOut } = useAuth();
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  // Fetch current user's profile for avatar
  const { data: profile } = useQuery({
    queryKey: ["my-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("admin_profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Filter nav items based on role
  const navItems = allNavItems.filter(item => !item.adminOnly || isAdmin);

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  // Show access denied if not admin or content manager
  if (!hasAdminAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-display font-bold mb-2">Access Denied</h1>
          <p className="text-muted-foreground mb-6">
            You don't have admin privileges. Contact an administrator to request access.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="outline" asChild>
              <Link to="/en">Go to Website</Link>
            </Button>
            <Button variant="ghost" onClick={signOut}>
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const displayName = profile?.display_name || user.email;
  const avatarInitial = profile?.display_name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || "?";

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-50">
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <ThemedLogo size="md" />
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Link 
              to="/en" 
              className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center"
            >
              ← Back to Website
            </Link>
            <ThemeCustomizer />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={profile?.avatar_url || undefined} alt={displayName || ""} />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {avatarInitial}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="flex items-center gap-3 px-2 py-2">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={profile?.avatar_url || undefined} alt={displayName || ""} />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {avatarInitial}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col overflow-hidden">
                    <span className="text-sm font-medium truncate">
                      {profile?.display_name || "No name"}
                    </span>
                    <span className="text-xs text-muted-foreground truncate">
                      {user.email}
                    </span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setProfileDialogOpen(true)}>
                  <User className="mr-2 h-4 w-4" />
                  Edit Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-background border-r border-border min-h-[calc(100vh-65px)] sticky top-[65px]">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <div key={item.path}>
                <Link
                  to={item.path}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                    isActive(item.path, item.exact) && !item.children?.some(c => isActive(c.path))
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  )}
                >
                  <item.icon size={18} />
                  {item.label}
                </Link>
                {item.children && isActive(item.path) && (
                  <div className="ml-7 mt-1 space-y-1">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={cn(
                          "flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors",
                          isActive(child.path)
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        )}
                      >
                        <child.icon size={14} />
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <Outlet />
        </main>
      </div>

      {/* Profile Edit Dialog */}
      <ProfileEditDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} />
    </div>
  );
};

export default AdminLayout;
